import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectSessionAdvisor } from '@/model/selectors';
import { resetAdvisorAdminPortal } from '@/model/advisorAdminPortalModel';
import { clearSubscriptionSeats } from '@/model/subscriptionSeatsModel';
import { isWizard } from '@shared/isWizard';

interface UseAdminModeOptions {
  propAdminMode?: boolean;
  propShowAdvisorSearch?: boolean;
}

export function useAdminMode({
  propAdminMode = false,
  propShowAdvisorSearch = false,
}: UseAdminModeOptions = {}) {
  const dispatch = useDispatch();
  const sessionAdvisor = useSelector(selectSessionAdvisor);
  const [selectedAdvisorId, setSelectedAdvisorId] = useState<string | null>(null);
  const [isAdminModeEnabled, setIsAdminModeEnabled] = useState(propAdminMode);

  // Check if current user is a wizard
  const isWizardUser = sessionAdvisor && isWizard(sessionAdvisor);

  // Admin mode is active if explicitly enabled via props OR user toggled it on
  const adminModeActive = propAdminMode || isAdminModeEnabled;
  const showSearch = propShowAdvisorSearch || isAdminModeEnabled;

  const handleAdvisorSelected = useCallback((advisor: Advisor) => {
    setSelectedAdvisorId(advisor.advisorId);
  }, []);

  const handleToggleChange = useCallback((checked: boolean) => {
    setIsAdminModeEnabled(checked);
    if (!checked) {
      // Reset when leaving admin mode
      setSelectedAdvisorId(null);
      dispatch(resetAdvisorAdminPortal());
      dispatch(clearSubscriptionSeats());
    }
  }, [dispatch]);

  const handleChangeAdvisor = useCallback(() => {
    // Clear selection and return to search
    setSelectedAdvisorId(null);
    dispatch(resetAdvisorAdminPortal());
    dispatch(clearSubscriptionSeats());
  }, [dispatch]);

  const hasSelectedAdvisor = Boolean(selectedAdvisorId);

  return {
    // State
    isWizardUser,
    isAdminModeEnabled,
    selectedAdvisorId,
    adminModeActive,
    showSearch,
    hasSelectedAdvisor,

    // Handlers
    handleAdvisorSelected,
    handleToggleChange,
    handleChangeAdvisor,
  };
}

